import React, { useEffect } from "react";
import { Menu, Spinner, Text } from "@chakra-ui/react";
import { useSelector } from "react-redux";
import {
  selectAllProperties,
  selectPropertiesLoading,
} from "@/stores/properties-store/properties-selectors";
import { useProperties } from "@/lib/hooks/use-properties";
import styles from "./property-select-menu.module.css";

interface PropertySelectProps {
  value?: string;
  onValueChange: (value: string | undefined) => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideGroupLabel?: boolean;
  showAllOption?: boolean;
}

export const PropertySelectMenu = ({
  value,
  onValueChange,
  isOpen,
  onOpenChange,
  hideGroupLabel,
  showAllOption = true,
}: PropertySelectProps) => {
  const properties = useSelector(selectAllProperties);
  const isLoading = useSelector(selectPropertiesLoading);
  const { loadProperties } = useProperties(false);

  // Only load properties when the menu is open
  useEffect(() => {
    if (isOpen && properties.length === 0 && !isLoading) {
      loadProperties();
    }
  }, [isOpen, properties.length, isLoading, loadProperties]);

  if (isLoading) {
    return (
      <div className={styles.selectTrigger}>
        <Spinner size='sm' mr={2} />
        <Text>Loading properties...</Text>
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className={styles.selectTrigger}>
        <Text>No properties available</Text>
      </div>
    );
  }

  // Build props for Menu.Root
  type MenuSelectDetails = { value: string };
  type MenuOpenChangeDetails = { open: boolean };
  const menuRootProps: Record<string, unknown> = {
    onSelect: (details: MenuSelectDetails) =>
      onValueChange(typeof details.value === "string" ? details.value : ""),
  };
  if (typeof isOpen === "boolean") menuRootProps.open = isOpen;
  if (typeof onOpenChange === "function")
    menuRootProps.onOpenChange = (details: MenuOpenChangeDetails) =>
      onOpenChange(details.open);

  return (
    <Menu.Root {...menuRootProps}>
      <Menu.Trigger asChild>
        <button className={styles.selectTrigger} type='button'>
          {value
            ? properties.find((p) => p.id === value)?.address
            : "All Properties"}
          <span style={{ marginLeft: 8 }}>&#9660;</span>
        </button>
      </Menu.Trigger>
      <Menu.Positioner>
        <Menu.Content>
          <Menu.ItemGroup>
            {/* Only show label if not hidden */}
            {!hideGroupLabel && (
              <Menu.ItemGroupLabel>Properties</Menu.ItemGroupLabel>
            )}
            {/* All Properties option at the top */}
            {showAllOption && (
              <Menu.Item value='' onSelect={() => onValueChange("")}>
                All Properties
              </Menu.Item>
            )}
            {properties.map((property) => (
              <Menu.Item
                key={property.id}
                value={property.id}
                onSelect={() => onValueChange(property.id)}
              >
                {property.address}
              </Menu.Item>
            ))}
          </Menu.ItemGroup>
        </Menu.Content>
      </Menu.Positioner>
    </Menu.Root>
  );
};
